import {
  CSSProperties,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
} from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { ExpandMore } from '@mui/icons-material';
import { Backdrop, Stack } from '@mui/material';
import { AnimatePresence } from 'framer-motion';

import { useUserStore } from '@/store';
import { getNewTabSearch } from '@/utils';

import { TabLayoutRoot } from './styles';
import {
  TabItem,
  TabLayoutContextProvider,
  useTabLayoutContext,
} from './TabLayoutContext';

interface TabLayoutProps {
  tabLinks: TabItem[];
  basePath: string;
  renderOnPage?: boolean;
  style?: CSSProperties;
  children?: ReactNode;
}

export const TabLayoutContent = ({
  basePath,
  tabLinks,
  renderOnPage,
  style,
  children,
}: TabLayoutProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { updateTabLayoutContextValues } = useTabLayoutContext();
  const normalizedBasePath = basePath.endsWith('/')
    ? basePath.slice(0, -1)
    : basePath;
  const currentNavTab = tabLinks.find(
    (pathName) => `${normalizedBasePath}/${pathName.path}` === pathname,
  );
  const { account } = useUserStore();

  useEffect(() => {
    updateTabLayoutContextValues({ basePath, tabLinks });
  }, [basePath, tabLinks]);

  useEffect(() => {
    updateTabLayoutContextValues({ currentNavTab });
  }, [currentNavTab]);

  const isEnabledRadioClicksModule =
    account?.settings?.features?.isEnabledRadioClicksModule;

  const handleClose = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const search = getNewTabSearch(searchParams);
    const searchValue = searchParams.get('search');
    const searchParam = searchValue
      ? `${search ? '&' : '?'}search=${encodeURIComponent(searchValue)}`
      : '';
    navigate(`${basePath}${search}${searchParam}`);
  }, [basePath, navigate]);

  return (
    <TabLayoutRoot
      className={currentNavTab ? 'active' : ''}
      style={style}
      bottom={isEnabledRadioClicksModule ? 20 : 0}
    >
      <Stack className="tablayout-container">
        {!!currentNavTab && (
          <>
            {!renderOnPage && (
              <Stack className="header" onClick={handleClose}>
                <ExpandMore color="error" />
              </Stack>
            )}
            <Stack
              className={`tablayout-content ${renderOnPage && 'renderOnPage'}`}
            >
              <AnimatePresence mode="wait">
                <Suspense fallback={<p>Loading package location...</p>}>
                  <Outlet key={basePath} />
                </Suspense>
              </AnimatePresence>
            </Stack>

            {!renderOnPage && (
              <Backdrop open={true} onClick={handleClose} className="overlay" />
            )}
          </>
        )}
      </Stack>
      {children}
    </TabLayoutRoot>
  );
};

export const TabLayout = (props: TabLayoutProps) => (
  <TabLayoutContextProvider>
    <TabLayoutContent {...props} />
  </TabLayoutContextProvider>
);
